// Estado em memória do backend falso para Notifications (M1 §7): templates com versões e preferências por
// usuário. Nenhum e-mail é realmente enviado; `test-send` só grava um registro (Parte "Recipient value never
// persisted" — mesmo no fake, o endereço completo não é guardado, para acostumar a UI com a forma real).
import type {
  MergeFieldDeclaration,
  NotificationPreferences,
  TemplateAudience,
  TemplateChannel,
  TemplateVersionStatus,
} from '../../modules/notifications/api/contract.preview';
import { nextId } from './workflowStore';

// Mutável (espelha `MessageTemplateVersion`, que é `readonly` — o contrato preview é a forma da resposta,
// não o estado interno da loja).
export interface FakeLocaleContent {
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
}

export interface FakeTemplateVersion {
  templateId: string;
  key: string;
  version: number;
  status: TemplateVersionStatus;
  mergeFields: MergeFieldDeclaration[];
  locales: { 'pt-BR': FakeLocaleContent; 'en-US': FakeLocaleContent };
  objectContentHash: string;
  approvalRequestId: string | null;
  changeReason: string | null;
  createdAt: string;
  createdBy: string;
  etag: string;
}

export interface FakeTemplate {
  id: string;
  key: string;
  channels: TemplateChannel[];
  audience: TemplateAudience;
  sender: string;
  mandatory: boolean;
  versions: FakeTemplateVersion[];
  /** Id da `FakeApproval` pendente (workflowStore) para a versão em revisão, se houver. */
  pendingApprovalId: string | null;
}

export interface FakeTestSendRecord {
  id: string;
  templateId: string;
  version: number;
  locale: string;
  recipientRef: string;
  sentAt: string;
}

export interface FakeNotificationsState {
  templates: FakeTemplate[];
  preferencesByUserId: Map<string, NotificationPreferences>;
  testSends: FakeTestSendRecord[];
}

function content(
  subjectPt: string,
  bodyPt: string,
  subjectEn: string,
  bodyEn: string,
): FakeTemplateVersion['locales'] {
  return {
    'pt-BR': { subject: subjectPt, bodyHtml: bodyPt, bodyText: null },
    'en-US': { subject: subjectEn, bodyHtml: bodyEn, bodyText: null },
  };
}

export function seedNotificationsState(): FakeNotificationsState {
  const templates: FakeTemplate[] = [
    {
      id: nextId('tpl'),
      key: 'task.assigned',
      channels: ['Email'],
      audience: 'Internal',
      sender: 'noreply@',
      mandatory: false,
      pendingApprovalId: null,
      versions: [
        {
          templateId: '',
          key: 'task.assigned',
          version: 1,
          status: 'Approved',
          mergeFields: [
            { name: 'recipientName', source: 'user.displayName' },
            { name: 'taskType', source: 'task.typeCode' },
            { name: 'dueAt', source: 'task.dueAt' },
          ],
          locales: content(
            'Nova tarefa: {{taskType}}',
            '<p>Olá {{recipientName}}, uma tarefa foi atribuída a você. Prazo: {{dueAt}}.</p>',
            'New task: {{taskType}}',
            '<p>Hello {{recipientName}}, a task was assigned to you. Due: {{dueAt}}.</p>',
          ),
          objectContentHash: 'sha256:seed-task-assigned-v1',
          approvalRequestId: null,
          changeReason: null,
          createdAt: new Date(Date.now() - 30 * 86_400_000).toISOString(),
          createdBy: 'Sistema',
          etag: 'W/"tpl-task-assigned-v1"',
        },
        {
          templateId: '',
          key: 'task.assigned',
          version: 2,
          status: 'InReview',
          mergeFields: [
            { name: 'recipientName', source: 'user.displayName' },
            { name: 'taskType', source: 'task.typeCode' },
            { name: 'dueAt', source: 'task.dueAt' },
          ],
          locales: content(
            'Nova tarefa para você: {{taskType}}',
            '<p>Olá {{recipientName}}, uma nova tarefa ({{taskType}}) precisa da sua atenção até {{dueAt}}.</p>',
            'A new task needs you: {{taskType}}',
            '<p>Hello {{recipientName}}, a new task ({{taskType}}) needs your attention by {{dueAt}}.</p>',
          ),
          objectContentHash: 'sha256:seed-task-assigned-v2',
          approvalRequestId: null,
          changeReason: 'Deixar o assunto mais claro para quem recebe muitas tarefas.',
          createdAt: new Date(Date.now() - 8 * 3_600_000).toISOString(),
          createdBy: 'Ana Souza',
          etag: 'W/"tpl-task-assigned-v2-0"',
        },
      ],
    },
    {
      id: nextId('tpl'),
      key: 'appointment.reminder',
      channels: ['Email', 'Sms'],
      audience: 'Participant',
      sender: 'agenda@',
      mandatory: false,
      pendingApprovalId: null,
      versions: [
        {
          templateId: '',
          key: 'appointment.reminder',
          version: 1,
          status: 'Approved',
          mergeFields: [
            { name: 'firstName', source: 'participant.firstName' },
            { name: 'visitDate', source: 'appointment.date' },
          ],
          locales: content(
            'Lembrete da sua consulta',
            '<p>Olá {{firstName}}, sua consulta é em {{visitDate}}.</p>',
            'Your appointment reminder',
            '<p>Hello {{firstName}}, your appointment is on {{visitDate}}.</p>',
          ),
          objectContentHash: 'sha256:seed-appointment-reminder-v1',
          approvalRequestId: null,
          changeReason: null,
          createdAt: new Date(Date.now() - 60 * 86_400_000).toISOString(),
          createdBy: 'Sistema',
          etag: 'W/"tpl-appointment-reminder-v1"',
        },
      ],
    },
    {
      id: nextId('tpl'),
      key: 'identity.security-alert',
      channels: ['Email'],
      audience: 'Internal',
      sender: 'seguranca@',
      mandatory: true,
      pendingApprovalId: null,
      versions: [
        {
          templateId: '',
          key: 'identity.security-alert',
          version: 1,
          status: 'Approved',
          mergeFields: [{ name: 'recipientName', source: 'user.displayName' }],
          locales: content(
            'Alerta de segurança da sua conta',
            '<p>Olá {{recipientName}}, detectamos uma atividade que merece sua atenção.</p>',
            'Security alert for your account',
            '<p>Hello {{recipientName}}, we detected activity that needs your attention.</p>',
          ),
          objectContentHash: 'sha256:seed-security-alert-v1',
          approvalRequestId: null,
          changeReason: null,
          createdAt: new Date(Date.now() - 90 * 86_400_000).toISOString(),
          createdBy: 'Sistema',
          etag: 'W/"tpl-security-alert-v1"',
        },
      ],
    },
  ];
  for (const template of templates) {
    for (const version of template.versions) {
      version.templateId = template.id;
    }
  }
  return { templates, preferencesByUserId: new Map(), testSends: [] };
}

export function defaultPreferences(): NotificationPreferences {
  return {
    channels: { email: true, sms: false },
    preferredLocale: null,
    doNotContactWindows: [],
    smsAvailable: false,
    mandatoryCategories: ['identity'],
    etag: 'W/"prefs-0"',
  };
}
